import React from "react";
import classNames from "classnames";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    DownloadIcon,
    FullScreenIcon,
    FullScreenExitIcon,
} from "@icons";
import { Separator } from "@design-system/Separator/Separator";
import { Tag } from "@design-system/Tag/Tag";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { splitFileName } from "@design-system/FileTypeIcon/fileType";
import styles from "./FinancialDocumentDetailsHeader.module.scss";
import { FinancialDocumentMetadata } from "./FinancialDocumentMetadata";
import { useDownloadFinancialDocumentFile } from "./useDownloadFinancialDocumentFile";
import { FinancialDocumentNoMatches } from "./FinancialDocumentNoMatches";
import { useFinancialDocumentDetailsContext } from "./financialDocumentDetails.context";

interface Props {
    financialDocument: FinancialDocumentExtended;
}

export const FinancialDocumentDetailsHeader: React.FC<Props> = ({
    financialDocument,
}) => {
    const { extension, name } = splitFileName(financialDocument.fileName);
    const { download, isDownloading } =
        useDownloadFinancialDocumentFile(financialDocument);
    const {
        isMaximized,
        toggleMaximized,
        shouldShowNavigation,
        canGoToPrevious,
        canGoToNext,
        goToPrevious,
        goToNext,
    } = useFinancialDocumentDetailsContext();

    const hasMatches = financialDocument.transactionMatches.length > 0;
    const documentName = (
        <h3 className={styles.heading}>
            {name}
            {extension && <small className="text-gray-500">.{extension}</small>}
        </h3>
    );

    let content: React.ReactNode;

    if (hasMatches) {
        content = (
            <section className={classNames(styles.section, styles.infoSection)}>
                <Tag color="neutral" outline>
                    Receipt matched
                </Tag>

                {documentName}

                <FinancialDocumentMetadata
                    financialDocument={financialDocument}
                />
            </section>
        );
    } else {
        content = (
            <>
                <section
                    className={classNames(styles.section, styles.infoSection)}
                >
                    {documentName}

                    <FinancialDocumentNoMatches
                        financialDocument={financialDocument}
                    />
                </section>

                <section
                    className={classNames(styles.section, styles.infoSection)}
                >
                    <FinancialDocumentMetadata
                        financialDocument={financialDocument}
                    />
                </section>
            </>
        );
    }

    return (
        <div
            className={styles.header}
            data-testid="financial-document-details-header"
        >
            <section
                className={classNames(styles.section, styles.titleSection)}
            >
                <h5 className={styles.title}>Document</h5>

                <aside className={styles.actions}>
                    {shouldShowNavigation && (
                        <>
                            <Tooltip content="Previous document">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    iconOnly
                                    onClick={goToPrevious}
                                    disabled={!canGoToPrevious}
                                    aria-label="Previous document"
                                >
                                    <ArrowUpIcon />
                                </Button>
                            </Tooltip>
                            <Tooltip content="Next document">
                                <Button
                                    variant="secondary"
                                    size="md"
                                    iconOnly
                                    onClick={goToNext}
                                    disabled={!canGoToNext}
                                    aria-label="Next document"
                                >
                                    <ArrowDownIcon />
                                </Button>
                            </Tooltip>
                            <Separator
                                orientation="vertical"
                                className={styles.navigationSeparator}
                            />
                        </>
                    )}
                    <Tooltip
                        content={
                            isMaximized ? "Exit full screen" : "Full screen"
                        }
                    >
                        <Button
                            variant="secondary"
                            size="md"
                            iconOnly
                            onClick={toggleMaximized}
                            aria-label={
                                isMaximized ? "Exit full screen" : "Full screen"
                            }
                        >
                            {isMaximized ? (
                                <FullScreenExitIcon />
                            ) : (
                                <FullScreenIcon />
                            )}
                        </Button>
                    </Tooltip>
                    <ButtonWithLoader
                        variant="secondary"
                        size="md"
                        iconOnly
                        aria-label="Download document"
                        onClick={() => {
                            void download();
                        }}
                        loading={isDownloading}
                        tooltip="Download"
                        data-testid="download-financial-document-file"
                    >
                        <DownloadIcon />
                    </ButtonWithLoader>
                </aside>
            </section>

            {content}
        </div>
    );
};
